import React, { useEffect, useRef, useState } from 'react';
import { Typography, Button, Spin, Space, theme } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import HeavyDutyMachineryCard from '../pages/HeavyDutyMachinery/HeavyDutyMachineryCard';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Title } = Typography;

const CARD_WIDTH = 260;
const SCROLL_STEP = 320;
const EDGE_FADE_WIDTH = 32;

interface HeavyDutyMachineryCarouselProps {
  title: string;
  spaces: IHeavyDutyMachinery[];
  loading: boolean;
  onSelect: (space: IHeavyDutyMachinery) => void;
  // Only passed by the "Near you" shelf -- see HeavyDutyMachineryCard's own
  // comment on why every other caller leaves this out.
  distanceById?: Record<string, number>;
}

/**
 * Horizontal, scrollable shelf of HeavyDutyMachineryCard -- the landing page's
 * "Featured this week" / "Top rated" / "Near you" sections. Renders nothing
 * once loaded with zero spaces, rather than an empty shelf or an error --
 * same reasoning DiscoverySpaces applies to hiding "Near you" entirely when
 * geolocation isn't available.
 *
 * On isMobile the card row itself (not the title/buttons above it) breaks
 * out of the page's own side padding to the true viewport edges -- the
 * `calc(50% - 50vw)` trick rather than a fixed negative margin, so it bleeds
 * correctly no matter what padding whatever ancestor page happens to use.
 * Cards get no inner padding either, so the first/last card touches the
 * screen edge directly instead of stopping short of it -- the Airbnb-style
 * mobile look this was asked to match. The scrollbar itself is hidden too
 * (no-scrollbar, see index.css) since a swipeable shelf has no business
 * showing one.
 */
const HeavyDutyMachineryCarousel: React.FC<HeavyDutyMachineryCarouselProps> = ({ title, spaces, loading, onSelect, distanceById }) => {
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Left fade only once actually scrolled past the start; right fade
  // whenever there's more content past the visible end -- both computed
  // fresh on every scroll/resize (see the effect below) rather than derived
  // once, since scrollWidth/clientWidth only exist once the row has laid
  // out its cards.
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateFade = () => {
      setShowLeftFade(el.scrollLeft > 0);
      // -1px tolerance for sub-pixel rounding at the exact end.
      setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    updateFade();
    el.addEventListener('scroll', updateFade, { passive: true });
    window.addEventListener('resize', updateFade);
    return () => {
      el.removeEventListener('scroll', updateFade);
      window.removeEventListener('resize', updateFade);
    };
    // Re-measure once the actual cards are in the DOM (loading -> loaded,
    // or the shelf's own data changing) -- scrollWidth is 0 either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, spaces]);

  if (!loading && spaces.length === 0) return null;

  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  // Fades to the section's own background (not transparent) so a
  // partially-cut card visually disappears into the page behind it rather
  // than showing a hard edge -- a plain opacity overlay, not mask-image, so
  // it can transition smoothly (browsers don't animate mask-image itself).
  const fadeOverlayStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 8,
    [side]: 0,
    width: EDGE_FADE_WIDTH,
    background: `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${token.colorBgContainer}, ${token.colorBgContainer}00)`,
    pointerEvents: 'none',
    opacity: side === 'left' ? (showLeftFade ? 1 : 0) : showRightFade ? 1 : 0,
    transition: 'opacity 200ms ease',
  });

  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        {!loading && spaces.length > 0 && (
          <Space>
            <Button shape="circle" icon={<LeftOutlined />} onClick={() => scrollBy(-SCROLL_STEP)} />
            <Button shape="circle" icon={<RightOutlined />} onClick={() => scrollBy(SCROLL_STEP)} />
          </Space>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            ...(isMobile ? { width: '100vw', marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' } : {}),
          }}
        >
          <div
            ref={scrollRef}
            className={isMobile ? 'no-scrollbar' : undefined}
            style={{
              display: 'flex',
              gap: 16,
              overflowX: 'auto',
              paddingBottom: 8,
              scrollSnapType: 'x mandatory',
              ...(isMobile ? { scrollbarWidth: 'none' as const, msOverflowStyle: 'none' } : {}),
            }}
          >
            {spaces.map((space) => (
              <div key={space.id} style={{ minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH, scrollSnapAlign: 'start' }}>
                <HeavyDutyMachineryCard space={space} onClick={() => onSelect(space)} distanceKm={distanceById?.[space.id]} />
              </div>
            ))}
          </div>
          <div style={fadeOverlayStyle('left')} />
          <div style={fadeOverlayStyle('right')} />
        </div>
      )}
    </section>
  );
};

export default HeavyDutyMachineryCarousel;
